import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";

// 首頁每一塊的位置與大小(業主 2026-09-17)。
//
// > 每個區塊 都要可以讓我們自由的拖拉大小長寬
// > 我覺得首頁的拖拉 做得不夠精緻 你參考iphone 或者是安卓小工具那種作法好不好
// > 把整個首頁畫成很多小小的格子可以調整每一個工具的寬高
// > 除了日曆 我想要其他工具都可以自由的托放位置/大小
//
// ==========================================================================
// 十二欄的細格子,每一塊記著 x / y / w / h
// ==========================================================================
// 第一版只有寬高,而且是「四欄、依序排下去」—— 想把時鐘搬到左上角是做不到的。
// 手機桌面的小工具不是那樣運作的:拖到哪就在哪。所以位置也要存,
// 而且格子要夠細(十二欄)才拖得準。
//
// ==========================================================================
// 存在帳號上(業主選的)
// ==========================================================================
// 不是 localStorage:他換一台電腦、換一個瀏覽器,版面要跟著人走。
// 後端那一支(`/api/users/me/home-layout`)**不認得**這些格子的意思,
// 只存形狀 —— 以後前端加一塊新的不必改後端、不必跑 migration。
//
// ==========================================================================
// 讀不到就用預設,**絕不白畫面**
// ==========================================================================
// 首頁是每個人每天第一眼看到的東西。一份存壞的版面、一次網路斷線,
// 都不該讓人打不開它。所以這裡每一條路徑最後都會給出一份能畫的版面。
//
// ==========================================================================
// 存回去的是**整份**,不是改到的那一塊
// ==========================================================================
// 只送一塊的話,後端那一份就會缺掉其他塊,而下次載入時缺的那幾塊全部
// 跳回預設 —— 使用者會覺得「我明明調過了」。

/** 一塊的位置與大小,單位是格。 */
export interface BlockSize {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /**
   * 關起來的區塊(業主 2026-09-17:「清單裡選要顯示哪幾塊、不要的移掉」)。
   *
   * **是一個旗標,不是把它從清單裡刪掉。** 刪掉的話,「下一版新加的區塊」
   * 與「使用者關掉的區塊」在資料上長得一模一樣(兩者都是「存檔裡沒有這一塊」)
   * —— 而新的那一塊本來就該自己出現,關掉的那一塊本來就不該。
   */
  hidden?: boolean;
  /** 日曆專屬:大中小。其他塊沒有這一欄。 */
  size?: string;
  /** 日曆專屬:格 / 列。 */
  view?: string;
}

/** 首頁的欄數。十二欄夠細,拖起來才像小工具而不是像排版。 */
export const COLUMNS = 12;

/** 一格有多高(像素)。 */
export const ROW_PX = 40;

/** 最小尺寸。**縮成一條線的區塊點不回來**,所以留得住的下限要明寫。 */
export const MIN_W = 2;
export const MIN_H = 3;
export const MAX_H = 40;

const PATH = "/users/me/home-layout";

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function intOr(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : fallback;
}

/** 把後端那一份(可能是任何東西)整理成乾淨的版面。 */
export function normalize(saved: unknown, defaults: BlockSize[]): BlockSize[] {
  const rows = Array.isArray(saved) ? saved : [];
  const found = new Map<string, Partial<BlockSize>>();
  for (const row of rows) {
    if (row === null || typeof row !== "object") continue;
    const one = row as Record<string, unknown>;
    const id = typeof one.id === "string" ? one.id : "";
    if (!id) continue;
    found.set(id, {
      ...(one.x === undefined ? {} : { x: intOr(one.x, 0) }),
      ...(one.y === undefined ? {} : { y: intOr(one.y, 0) }),
      ...(one.w === undefined ? {} : { w: intOr(one.w, MIN_W) }),
      ...(one.h === undefined ? {} : { h: intOr(one.h, MIN_H) }),
      ...(typeof one.size === "string" ? { size: one.size } : {}),
      ...(typeof one.view === "string" ? { view: one.view } : {}),
      ...(typeof one.hidden === "boolean" ? { hidden: one.hidden } : {}),
    });
  }

  // **以 defaults 的順序與名單為準**:後端那一份裡不認得的 id 丟掉
  // (那是舊版本留下的塊),沒存到的用預設補上(那是這一版新加的塊)。
  return defaults.map((base) => {
    const merged: BlockSize = { ...base, ...(found.get(base.id) ?? {}) };
    return {
      ...merged,
      x: clamp(merged.x, 0, COLUMNS - MIN_W),
      y: Math.max(0, merged.y),
      w: clamp(merged.w, MIN_W, COLUMNS),
      h: clamp(merged.h, MIN_H, MAX_H),
    };
  });
}

/** 拖曳或拉動之後回報的一塊。 */
export interface Placed {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface HomeLayout {
  blocks: BlockSize[];
  /** 改一塊的選項(日曆的大小與格/列)。**整份存回去**,見檔頭。 */
  update: (id: string, patch: Partial<Omit<BlockSize, "id">>) => void;
  /** 相對調整大小,方向鍵走它。 */
  resize: (id: string, dw: number, dh: number) => void;
  /** 拖曳或拉動之後,把新的位置整份收下來。 */
  place: (next: Placed[]) => void;
}

export function useHomeLayout(defaults: BlockSize[]): HomeLayout {
  const [blocks, setBlocks] = useState<BlockSize[]>(defaults);
  // **使用者動過之後,那一趟還在路上的 GET 就不准再覆蓋畫面。**
  // 首頁一載入就發出 GET,而使用者完全可能在它回來之前就拖了一下 ——
  // 那時讓後端那一份贏,他會看到自己剛擺好的東西又跳回去。
  const touched = useRef(false);
  // **第一趟讀回來之前,版面元件自己算出來的位置一律不算數。**
  //
  // 格子在掛載的當下就會回報一次版面(它會把重疊的往下推、把空洞往上收),
  // 而那一次算的是**預設版面**。收下它就等於「使用者動過了」——
  // 於是後端那一份回來時會被當成過期資料丟掉,他上次擺好的位置全部消失。
  // 這個症狀每次開首頁都會發生,而且完全沒有錯誤訊息。
  const loaded = useRef(false);

  useEffect(() => {
    let alive = true;
    api
      .get<{ blocks?: unknown }>(PATH)
      .then((got) => {
        if (!alive || touched.current) return;
        setBlocks(normalize(got?.blocks, defaults));
      })
      .catch(() => {
        /* 讀不到就用預設 —— 見檔頭,首頁絕不白畫面 */
      })
      .finally(() => {
        loaded.current = true;
      });
    return () => {
      alive = false;
    };
    // defaults 是呼叫端的常數。放進相依會讓每次 render 都重讀一次。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // **認得的塊變了就跟著補。**
  //
  // 老闆比同仁多一塊「出勤總表」,而角色是登入之後才知道的;`blocks` 只在
  // 第一次 render 時抓了一份 defaults,不跟著走的話,登入後多出來的那一塊
  // 永遠不會出現在畫面上 —— 而且完全沒有錯誤訊息。
  //
  // 相依放 id 的字串而不是陣列本身:陣列每次 render 都是新的,放它會無限重跑。
  const ids = defaults.map((one) => one.id).join(",");
  useEffect(() => {
    setBlocks((prev) =>
      prev.map((one) => one.id).join(",") === ids ? prev : normalize(prev, defaults),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const save = useCallback((next: BlockSize[]) => {
    // 存不回去不影響眼前這一頁:畫面已經照 next 畫了,下次載入才會退回舊的。
    // 這裡不跳 toast —— 拖一下是一個連續的動作,每動一格跳一次會很吵。
    void api.put(PATH, { blocks: next }).catch(() => {});
  }, []);

  const update = useCallback(
    (id: string, patch: Partial<Omit<BlockSize, "id">>) => {
      touched.current = true;
      setBlocks((prev) => {
        const next = prev.map((one) => (one.id === id ? { ...one, ...patch } : one));
        save(next);
        return next;
      });
    },
    [save],
  );

  const resize = useCallback(
    (id: string, dw: number, dh: number) => {
      touched.current = true;
      setBlocks((prev) => {
        const next = prev.map((one) => {
          if (one.id !== id) return one;
          const w = clamp(one.w + dw, MIN_W, COLUMNS);
          return {
            ...one,
            w,
            // **撐到右邊界就往左讓,不是卡住。** 卡住的話,停在右邊那一塊
            // 永遠變不寬 —— 而使用者按了方向鍵卻什麼都沒發生,只會再按十下。
            x: Math.min(one.x, COLUMNS - w),
            h: clamp(one.h + dh, MIN_H, MAX_H),
          };
        });
        save(next);
        return next;
      });
    },
    [save],
  );

  const place = useCallback(
    (next: Placed[]) => {
      // 還沒讀回來就不收 —— 見 `loaded` 的說明。
      if (!loaded.current) return;
      setBlocks((prev) => {
        const byId = new Map(next.map((one) => [one.id, one]));
        const merged = prev.map((one) => {
          const found = byId.get(one.id);
          if (!found) return one;
          return { ...one, x: found.x, y: found.y, w: found.w, h: found.h };
        });
        // **沒有真的變就不要存。** 版面元件在第一次量到寬度、以及每次重繪
        // 之後都會回報一次版面 —— 照單全收的話,光是打開首頁就會送出一趟
        // PUT,而且那一趟送的可能是還沒讀回來的預設值。
        const same = merged.every((one, index) => {
          const before = prev[index];
          return (
            before.x === one.x && before.y === one.y && before.w === one.w && before.h === one.h
          );
        });
        if (same) return prev;
        touched.current = true;
        save(merged);
        return merged;
      });
    },
    [save],
  );

  return { blocks, update, resize, place };
}
